import { createClient, type SupabaseClient, type Session } from "@supabase/supabase-js";
import type { SupabaseDatabase } from "@garden/types/supabase";

export type Db = SupabaseClient<SupabaseDatabase>;

/* Supabase client + auth helpers.
 *
 * Env vars are read from import.meta.env (Vite). They must be prefixed
 * VITE_ to be exposed to the browser. Anon key is safe to ship to the client;
 * the service-role key is NOT and lives only in apps/worker.
 *
 * If env vars are missing we export `null` and let the UI render a clear
 * error rather than crashing on import. This keeps the dev server runnable
 * even before Supabase is set up.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const supabaseConfigured: boolean = Boolean(url && anonKey);

export const supabase: Db | null = supabaseConfigured
  ? createClient<SupabaseDatabase>(url!, anonKey!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

export type AuthSession = Session;

export async function signInWithMagicLink(email: string): Promise<void> {
  if (!supabase) throw new Error("Supabase is not configured. See infra/README.md.");
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: window.location.origin,
    },
  });
  if (error) throw error;
}

export async function signOut(): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
